import React, { forwardRef } from 'react';
import Select, { components, OptionProps, Props as SelectProps, StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import classNames from 'classnames';
import ErrorMessage from './ErrorMessage';
import InfoMessage from './InfoMessage';
import { DISABLED_INPUT } from './inputTheme';

interface CustomSelectProps extends SelectProps {
	label: React.ReactNode;
	required?: boolean;
	error?: string;
	className?: string;
	tooltip?: string;
	description?: string;
	renderOption?: (option: any) => React.ReactNode;
	isCreatable?: boolean;
	onCreateOption?: (inputValue: string) => void;
	/**
	 * When the menu opens, scroll the relevant option into view: the current selection when there
	 * is one (centred), otherwise the first selectable (non-disabled) option. Useful for lists
	 * where leading options are disabled (e.g. vehicle lengths below the minimum), so the user
	 * lands on their current choice / the first valid choice rather than a wall of greyed-out
	 * options.
	 */
	scrollToFirstEnabledOption?: boolean;
	/**
	 * Rendered on the field's outer wrapper div. react-select drops unknown DOM props, so a
	 * `data-test-id` spread onto it silently vanishes — which is why call sites used to wrap the
	 * whole field in their own `<div data-test-id="...">`. Passing it here puts it on the same
	 * element that wrapper would have been, so `[data-test-id='x'] .react-select__control`
	 * keeps working and e2e flows don't have to reach for the visible label text.
	 */
	'data-test-id'?: string;
}

export const getSelectStyles = (
	baseStyles: StylesConfig | undefined,
	error?: string,
	isDisabled?: boolean,
): StylesConfig => ({
	...baseStyles,
	control: (base, state) => {
		const customStyles =
			typeof baseStyles?.control === 'function' ? baseStyles.control(base, state) : baseStyles?.control;

		let borderColor = '#d1d5db';
		if (isDisabled) borderColor = DISABLED_INPUT.border;
		else if (error) borderColor = '#f87171';
		else if (state.isFocused) borderColor = 'var(--colour-brand-primary)';

		let hoverBorderColor = '#9ca3af';
		if (isDisabled) hoverBorderColor = '#d1d5db';
		else if (error) hoverBorderColor = '#f87171';
		else if (state.isFocused) hoverBorderColor = 'var(--colour-brand-primary)';

		let boxShadow = 'none';
		if (state.isFocused) {
			if (error) {
				boxShadow = '0 0 0 1px #ef4444';
			} else {
				boxShadow = '0 0 0 1px var(--colour-brand-primary)';
			}
		}

		return {
			...base,
			borderColor,
			boxShadow,
			borderWidth: '1px',
			borderStyle: 'solid',
			'&:hover': {
				borderColor: hoverBorderColor,
			},
			// Matched to the `New` text inputs beside them — measured, not derived: those inputs land on
			// 42px and an 8px radius, because unlayered legacy SCSS overrides both the line-height
			// `text-sm` asks for and the radius `rounded-md` asks for. Reading the Tailwind classes
			// alone gives 46px and 6px, which is what this used to be set to and why the two never
			// lined up. Check against a `TextInput` in the browser before changing either number.
			borderRadius: '8px',
			minHeight: '42px',
			fontSize: '14px',
			backgroundColor: isDisabled ? DISABLED_INPUT.background : 'white',
			...customStyles,
		};
	},
	/**
	 * The chosen option's text. Only worth saying anything about while the select is disabled —
	 * react-select's own disabled grey is lighter than the one the `New` text inputs use, which put a
	 * disabled ferry and a disabled charter name at two different weights in the same form.
	 */
	singleValue: (base, state) => {
		const customStyles =
			typeof baseStyles?.singleValue === 'function' ? baseStyles.singleValue(base, state) : baseStyles?.singleValue;

		return {
			...base,
			...(isDisabled ? { color: DISABLED_INPUT.text } : {}),
			...customStyles,
		};
	},
	input: (base, state) => {
		const customStyles =
			typeof baseStyles?.input === 'function' ? baseStyles.input(base, state) : baseStyles?.input;
		return {
			...base,
			boxShadow: 'none',
			'> input': {
				boxShadow: 'none !important',
				border: 'none !important',
				outline: 'none !important',
				fontSize: '0.875rem !important',
			},
			'input:focus': {
				boxShadow: 'none !important',
			},
			...customStyles,
		};
	},
	multiValue: (base, state) => {
		const customStyles =
			typeof baseStyles?.multiValue === 'function' ? baseStyles.multiValue(base, state) : baseStyles?.multiValue;
		return {
			...base,
			backgroundColor: '#F5F5F5',
			borderRadius: '52px',
			padding: '0px 6px',
			border: '1px solid #D9D9D9',
			...customStyles,
		};
	},
	multiValueLabel: (base, state) => {
		const customStyles =
			typeof baseStyles?.multiValueLabel === 'function'
				? baseStyles.multiValueLabel(base, state)
				: baseStyles?.multiValueLabel;
		return {
			...base,
			fontSize: '14px',
			fontWeight: 500,
			color: '#010C20',
			...customStyles,
		};
	},
	multiValueRemove: (base, state) => {
		const customStyles =
			typeof baseStyles?.multiValueRemove === 'function'
				? baseStyles.multiValueRemove(base, state)
				: baseStyles?.multiValueRemove;
		return {
			...base,
			color: '#010C20',
			'&:hover': {
				backgroundColor: 'transparent',
				color: '#374151',
			},
			...customStyles,
		};
	},
	menu: provided => ({
		...provided,
		borderRadius: '0.5rem', // rounded-lg
		boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // shadow-md
		marginTop: '4px',
		padding: '4px',
		fontSize: '0.875rem', // text-sm
	}),
	option: (provided, state) => {
		let backgroundColor = 'transparent';
		if (state.isSelected) {
			backgroundColor = 'color-mix(in srgb, var(--colour-brand-primary), white 90%)'; // lighter brand primary
		} else if (state.isFocused) {
			backgroundColor = '#f3f4f6'; // gray-100
		}

		return {
			...provided,
			backgroundColor,
			color: state.isDisabled ? '#9ca3af' : '#111827', // gray-400 when disabled, else gray-900
			fontWeight: state.isSelected ? 500 : 400,
			cursor: state.isDisabled ? 'not-allowed' : 'pointer',
			borderRadius: '0.25rem', // rounded
			marginBottom: '2px',
			fontSize: '0.875rem', // text-sm
			'&:active': {
				backgroundColor: 'color-mix(in srgb, var(--colour-brand-primary), white 80%)', // slightly darker than selected
			},
		};
	},
	menuPortal: base => ({
		...base,
		zIndex: 9999,
	}),
});

export const CustomOption = (
	customOptionProps: OptionProps<any, boolean> & { renderOption?: (option: any) => React.ReactNode },
) => {
	const { data, children, renderOption } = customOptionProps;
	return <components.Option {...customOptionProps}>{renderOption ? renderOption(data) : children}</components.Option>;
};

export const SelectFieldWrapper = ({
	label,
	required,
	error,
	className,
	isDisabled,
	tooltip,
	description,
	children,
	'data-test-id': dataTestId,
}: {
	label: React.ReactNode;
	required?: boolean;
	error?: string;
	className?: string;
	isDisabled?: boolean;
	tooltip?: string;
	description?: string;
	children: React.ReactNode;
	'data-test-id'?: string;
}) => (
	<div className={classNames('flex flex-col gap-1 w-full', className)} data-test-id={dataTestId}>
		{label && (
			<div className="flex items-center gap-1">
				{/* The label keeps its colour while the field is disabled, the same as `TextInput`'s and
				    `TextArea`'s — dimming it too made a disabled select read as a whole row switched off. */}
				<label className="text-sm text-gray-800">
					{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
				</label>
				{tooltip && (
					<div className="relative group">
						<span className="material-symbols-outlined text-gray-400 text-sm leading-none cursor-help">
							help
						</span>
						<div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-10 pointer-events-none">
							{tooltip}
						</div>
					</div>
				)}
			</div>
		)}
		{children}
		{/* mt-1.5 on top of the wrapper's gap-1 — 4px reads as touching the input. */}
		{description && <InfoMessage message={description} className="mt-1.5" />}
		<ErrorMessage error={error} />
	</div>
);

export const SelectInput = forwardRef<any, CustomSelectProps>(
	(
		{
			label,
			required,
			error,
			className,
			tooltip,
			description,
			renderOption,
			isCreatable,
			onCreateOption,
			scrollToFirstEnabledOption,
			'data-test-id': dataTestId,
			...props
		},
		ref,
	) => {
		const selectStyles = getSelectStyles(props.styles, error, props.isDisabled);

		const componentsOverride = {
			Option: (optionProps: any) => <CustomOption {...optionProps} renderOption={renderOption} />,
			IndicatorSeparator: () => null,
		};

		const SelectComponent = isCreatable ? CreatableSelect : Select;

		const handleMenuOpen = () => {
			props.onMenuOpen?.();
			if (!scrollToFirstEnabledOption) {
				return;
			}
			// react-select's built-in scroll-to-selected is unreliable with a portalled menu, so we
			// position the menu ourselves. Defer to the next tick so the portalled menu has rendered
			// its options before we query. Land on the current selection when there is one (centred so
			// a few options show either side), otherwise on the first selectable option — which skips
			// the leading disabled entries (e.g. vehicle lengths below the minimum).
			setTimeout(() => {
				const menus = document.querySelectorAll('.react-select__menu');
				const menu = menus[menus.length - 1];
				if (!menu) {
					return;
				}
				const selected = menu.querySelector('.react-select__option--is-selected');
				if (selected) {
					selected.scrollIntoView({ block: 'center', inline: 'nearest' });
					return;
				}
				const firstEnabled = menu.querySelector(
					'.react-select__option:not(.react-select__option--is-disabled)',
				);
				firstEnabled?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
			}, 0);
		};

		return (
			<SelectFieldWrapper
				label={label}
				required={required}
				error={error}
				className={className}
				isDisabled={props.isDisabled}
				tooltip={tooltip}
				description={description}
				data-test-id={dataTestId}
			>
				<SelectComponent
					{...props}
					ref={ref}
					className="react-select"
					styles={selectStyles}
					components={componentsOverride}
					onMenuOpen={handleMenuOpen}
					menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
					menuPlacement={props.menuPlacement ?? 'auto'}
					closeMenuOnSelect={props.closeMenuOnSelect ?? (props.isMulti ? false : undefined)}
					classNamePrefix="react-select"
					{...(isCreatable && onCreateOption ? { onCreateOption } : {})}
				/>
			</SelectFieldWrapper>
		);
	},
);
